import React, { useState } from "react";
import {
  Flex,
  Text,
  Box,
  SimpleGrid,
  Alert,
  AlertIcon,
  AlertTitle,
  Button
} from "@chakra-ui/react";
import { bookingFlowStore } from "../../store/BookingFlowStore";
import { doctorsStore } from "../../store/DoctorsStore";
import { sessionManager } from "../../managers/SessionManager";
import CardSkeleton from "../../components/CardSkeleton";
import { appointmentTypesStore } from "../../store/AppointmentTypesStore";
import sanitizeHtml from "sanitize-html";
import DoctorCard from "../DoctorCardBookingFlow";
import { Buffer } from "buffer";

const Step6_DoctorSelection: React.FC = () => {
  const [showFull, setShowFull] = useState(false);

  const appointmentType = bookingFlowStore.appointmentData.appointmentType;
  const instruction = appointmentTypesStore.selectedAptType?.appt_inst;

  const decodedMessage = appointmentType?.appt_que
    ? sanitizeHtml(
      Buffer.from(appointmentType.appt_que, "base64").toString("utf8")
    )
    : "";

  const selectedDoctor = bookingFlowStore.appointmentData.selectedDoctor;
  const isDoctorInList = doctorsStore.selectedAptTypeDoctors?.some(
    (doc) => doc.doctor_id === selectedDoctor?.doctor_id
  );

  if (selectedDoctor && isDoctorInList) {
    bookingFlowStore.goToStep(7);
    bookingFlowStore.setActiveStep(7);
    return null;
  }

  return (
    <Flex flexDirection="column" alignItems="center" justifyContent="center" width="100%" >
      <Text fontSize="lg" fontWeight="bold" mb={4}>
        Select your Doctor and time
      </Text>

      {decodedMessage?.trim() && (
        <Alert
          status="info"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          width={{ base: "100%", lg: "90%" }}
          p={3}
          bg="blue.100"
          borderRadius="md"
        >
          <AlertIcon boxSize="30px" mr={0} mb={2} />

          <Box
            noOfLines={showFull ? undefined : 2}
            dangerouslySetInnerHTML={{ __html: decodedMessage }}
            fontSize="sm"
            mb={2}
          />

          {!showFull && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              onClick={() => setShowFull(true)}
              borderRadius="full"
              fontWeight="bold"
            >
              More
            </Button>
          )}
        </Alert>
      )}



      <Box
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        fontWeight="bold"
        width={{ base: "100%", lg: "90%" }}
      >
        <p dangerouslySetInnerHTML={{ __html: instruction }} />
      </Box>


      {/* Error if selected doctor is not in list */}
      {selectedDoctor && !isDoctorInList && (
        <Alert status="error" mb={4} width={{ base: "100%", lg: "90%" }}>
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>
              Your selected doctor is not available for this appointment type.
              Please select another doctor from the list below.
            </AlertTitle>
          </Box>
        </Alert>
      )}

      {sessionManager.isLoadingDoctors ? (
        <Box width={{ base: "100%", lg: "90%" }} overflowY="auto" height="100vh">
          <SimpleGrid columns={{ base: 1, md: 1, lg: 1, xl: 1 }} spacing={5}>
            {Array.from({ length: 3 }).map((_, index) => (
              <CardSkeleton key={index} />
            ))}
          </SimpleGrid>
        </Box>
      ) : (
        <>
          {doctorsStore.selectedAptTypeDoctors?.length === 0 && (
            <Text>No doctors available for this appointment type.</Text>
          )}
          <Box width={{ base: "100%", lg: "90%" }} >
            <SimpleGrid columns={{ base: 1, md: 1, lg: 1, xl: 1 }} spacing={5}>
              {doctorsStore.selectedAptTypeDoctors?.map((doctor) => {
                if (
                  !doctor.display_name ||
                  !doctor.about ||
                  !doctor.image ||
                  !doctor.gender ||
                  !doctor.doctor_id ||
                  doctor.my_patients === undefined ||
                  doctor.display_only === undefined ||
                  doctor.no_new_patients === undefined
                ) {
                  return null;
                }

                return (
                  <DoctorCard
                    key={doctor.doctor_id}
                    doctor={{
                      display_name: doctor.display_name,
                      about: doctor.about,
                      image: doctor.image,
                      gender: doctor.gender,
                      doctor_id: doctor.doctor_id,
                      my_patients: doctor.my_patients,
                      display_only: doctor.display_only,
                      no_new_patients: doctor.no_new_patients,
                    }}
                    ddate={{
                      fadate: doctorsStore.selectedAptTypeFirstAvailableTimes?.[doctor.doctor_id]?.date || "Unavailable",
                      fatime: doctorsStore.selectedAptTypeFirstAvailableTimes?.[doctor.doctor_id]?.time || "Unavailable",
                    }}
                    duration={"15"}
                  />
                );
              })}
            </SimpleGrid>
          </Box>
        </>
      )}
    </Flex>
  );
};

export default Step6_DoctorSelection;
